import React, { useCallback, useMemo, useState } from 'react'
import PropTypes, { object } from 'prop-types'
import { useTranslation } from 'react-i18next'

import PortalModal from 'components/Shared/PortalModal'
import ErrorMessage from 'components/Shared/ErrorMessage'

import { actions, hasRequiredGoogleExactRatios, getMissingGoogleExactRatios } from './media'
import { getRequiredSlotRatios, getGoogleExactRatios } from './requiredSlotRatios'
import { postGeneratedImages } from 'api/generate-images'

import { applyGeneratedVariantsToMedia } from './applyGeneratedVariantsToMedia'
import {
  buildPostGeneratedImagesBody,
  collectSelectedImageIdsFromResult,
  mapGeneratedImagesResponse,
  normalizePostGeneratedImagesResult,
} from './elements/CoCreatePanel/generateImagesTransforms'

import { MediaExpandedModal } from './elements/MediaExpandedModal'
import GeneratedImagesModal from './elements/CoCreatePanel/GeneratedModal'
import { useMediaUploadService } from './MediaUpload.service'
import CoCreateImagePanel from './elements/CoCreatePanel/CoCreateImagePanel'
import useCoCreateImagePanel from './elements/CoCreatePanel/useCoCreateImagePanel'
import MediaListV2 from './elements/MediaListV2/MediaList'
import { UploadButtons } from './elements/UploadButtons'
import { LibraryPickerModal } from 'components/Library/element/LibraryPickerModal'
import YouTubeUrlInput from './YouTube/YouTubeUrlInput'
import { useCampaignSetup } from '../Context/CampaignSetupContext'
import { derivePlatformSelectionFromSetup, platformIncludesGoogle, platformIncludesMeta, SetupPlatform } from '../platform'
import { deriveCampaignTypeFromSetup } from 'components/Recommendations/EventCampaigns/Steps/Setup/Goals/campaignType'

const MediaUpload = ({
  initialMedia,
  onChange,
  readonly,
  maxItems = Number.MAX_VALUE,
  generatedMediaCount = 0,
  isCampaignRunning,
  eventId,
  tc,
  tcRunId,
  headlines = [],
  onRequestHeadlines,
  showYoutubeUrlInput = false,
  showValidationErrors = false,
  onClearValidationErrors,
}) => {
  const { t } = useTranslation()

  const [slotError, setSlotErrorState] = useState(null)

  // Slot-level errors (per-creative slot upload/replace) and the main-button
  // upload errors live in different components, so a new error on one side
  // never used to clear the other — both stayed on screen at once. Wrapping
  // both entry points here keeps only the most recent error visible,
  // regardless of which upload path produced it.
  const setSlotError = useCallback((error) => {
    setSlotErrorState(error)
    if (error) {
      onClearValidationErrors?.()
    }
  }, [onClearValidationErrors])

  const { setup } = useCampaignSetup()
  const platform = useMemo(() => derivePlatformSelectionFromSetup(setup), [setup])
  const isGoogleOnly = platform === SetupPlatform.google
  // Unlike isGoogleOnly, this also covers Meta+Google — the exact-ratio
  // rules below apply whenever Google is part of the selection at all.
  const isGoogleSelected = platformIncludesGoogle(platform)
  const isMetaSelected = platformIncludesMeta(platform)
  const bothPlatformsSelected = isGoogleSelected && isMetaSelected
  const campaignType = useMemo(() => deriveCampaignTypeFromSetup(setup), [setup])
  const requiredSlotRatios = useMemo(
    () => getRequiredSlotRatios(platform, campaignType),
    [platform, campaignType],
  )
  // Ratios Google itself needs exact — a subset of requiredSlotRatios.
  // Google Display's 9:16 slot (present only to cover Meta's Reels
  // placement) is deliberately excluded — Google never uses it.
  const googleExactRatios = useMemo(
    () => getGoogleExactRatios(platform, campaignType),
    [platform, campaignType],
  )

  // A main-button/library upload attempt is a fresh signal that supersedes
  // whatever slot error was showing — clear it here so only the latest
  // error (whichever side produced it) stays visible.
  const handleMediaChange = useCallback((...args) => {
    setSlotErrorState(null)
    onChange(...args)
  }, [onChange])

  const {
    media,
    generatedMediaCount: liveGeneratedMediaCount,
    reachedMaxLimit,
    expandedImage,
    isFirstItem,
    isLastItem,
    dispatch,
    onNext,
    onBack,
    removeFile,
    removeMediaGroup,
    handleAddFileToCoCreateGroup,
    addLibraryItemToCoCreateGroup,
    replaceLibraryItemInCoCreateGroup,
    handleReplaceFileInCoCreateGroup,
    handleRemoveFileFromCoCreateGroup,
    getInputProps,
    open,
    setExpandedImage,
    addLibraryItems,
    addYoutubeVideo,
  } = useMediaUploadService({
    initialMedia,
    onChange: handleMediaChange,
    maxItems,
    generatedMediaCount,
    isCampaignRunning,
    eventId,
    tc,
    tcRunId,
    isGoogleOnly,
    isGoogleSelected,
    googleExactRatios,
    requiredSlotRatios,
  })

  const [selectedSubGroupId, setSelectedSubGroupId] = useState(null)
  const [isLibraryPickerOpen, setIsLibraryPickerOpen] = useState(false)
  const [generatedImagesModalOpen, setGeneratedImagesModalOpen] = useState(false)
  const [isSubmittingSelections, setIsSubmittingSelections] = useState(false)

  const clearGenerationReserve = useCallback(() => {
    dispatch({ type: actions.clearMediaGenerating })
  }, [dispatch])

  const {
    variant: coCreateVariant,
    setVariant: setCoCreateVariant,
    generateImages,
    generatedResult,
    generationFailed,
    fetchStatusFailed,
    checkStatus,
  } = useCoCreateImagePanel({
    eventId,
    tc,
    tcRunId,
    onClearGenerating: clearGenerationReserve,
  })

  const generatedVariants = useMemo(() => {
    const mapped = mapGeneratedImagesResponse(generatedResult ?? [])
    return mapped.length > 0 ? mapped : []
  }, [generatedResult])

  const handleSubmitGeneratedSelections = async (selectedIds) => {
    if (!generatedResult) {
      return { success: false, error: 'No generated images to submit.' }
    }

    const body = buildPostGeneratedImagesBody({
      eventId,
      tc,
      tcRunId,
      generatedResult,
      selectedIds,
    })

    const response = await postGeneratedImages(body)

    if (!response.success) {
      return response
    }

    if (selectedIds.length > 0) {
      const result = normalizePostGeneratedImagesResult(response.data)

      if (!result) {
        return { success: false, error: 'Invalid response from server.' }
      }

      const variants = mapGeneratedImagesResponse(result)
      const selectedFromResponse = collectSelectedImageIdsFromResult(result)

      if (!selectedFromResponse.length) {
        return { success: false, error: 'No selected images returned from server.' }
      }

      const nextMedia = applyGeneratedVariantsToMedia(
        media,
        variants,
        selectedFromResponse,
        requiredSlotRatios,
      ).map((file) => (file.generating ? { ...file, generating: false } : file))

      dispatch({ type: actions.applyGeneratedVariants, payload: nextMedia })
      setSelectedSubGroupId(null)
    } else {
      dispatch({ type: actions.clearMediaGenerating })
    }

    setCoCreateVariant('open')
    await checkStatus()

    return { success: true }
  }

  const handleGenerate = async (params) => {
    setSelectedSubGroupId(null)
    const response = await generateImages(params)

    if (response.success && response.groupId) {
      dispatch({
        type: actions.setMediaGenerating,
        payload: { id: response.groupId, count: params.headlines?.length ?? 0 },
      })
    }

    return response
  }

  const handleRemoveOriginal = (item) => {
    removeFile(item.id)
  }

  const handleRemoveSubGroup = (subGroupId) => {
    removeMediaGroup(subGroupId)
  }

  const handleReplaceOriginal = (item) => {
    dispatch({ type: actions.startReplace, payload: item })
    open()
  }

  const handleDeviceUpload = () => {
    if (readonly || isCampaignRunning || reachedMaxLimit) {
      return
    }

    open()
  }

  const handleLibraryUpload = () => {
    if (readonly || isCampaignRunning || reachedMaxLimit) {
      return
    }

    setIsLibraryPickerOpen(true)
  }

  const handleLibraryApply = (selectedItems) => {
    addLibraryItems(selectedItems)
  }

  const handleAddToCoCreateSlot = async (file, groupId, requiredRatio, originalId = null) => {
    await handleAddFileToCoCreateGroup(file, groupId, requiredRatio, originalId, requiredSlotRatios)
  }

  const handleAddLibraryToCoCreateSlot = (item, groupId, requiredRatio, originalId = null) => {
    addLibraryItemToCoCreateGroup(item, groupId, requiredRatio, originalId)
  }

  const handleReplaceLibraryInCoCreateSlot = (item, fileToReplaceId) => {
    replaceLibraryItemInCoCreateGroup(item, fileToReplaceId)
  }

  const handleReplaceInCoCreateSlot = async (file, fileToReplaceId, requiredRatio) => {
    await handleReplaceFileInCoCreateGroup(file, fileToReplaceId, requiredRatio, requiredSlotRatios)
  }

  const handleRemoveFromCoCreateSlot = async (fileId) => {
    await handleRemoveFileFromCoCreateGroup(fileId)
  }

  const hasGoogleExactRatios = hasRequiredGoogleExactRatios(media)
  // Global across this TC's whole media array (all creatives combined),
  // not per-creative — mirrors hasRequiredGoogleExactRatios exactly, so a
  // slot border reflects the same thing that's actually gating Next.
  const missingGoogleExactRatios = useMemo(() => getMissingGoogleExactRatios(media), [media])

  return (
    <div>
      <MediaListV2
        media={media}
        selectiveMode={coCreateVariant === 'open'}
        selectedSubGroupId={selectedSubGroupId}
        setSelectedSubGroupId={setSelectedSubGroupId}
        isCoCreateReady={coCreateVariant === 'ready'}
        readonly={readonly}
        onRemoveOriginal={handleRemoveOriginal}
        onRemoveSubGroup={handleRemoveSubGroup}
        onReplaceOriginal={handleReplaceOriginal}
        onAddToCoCreateSlot={handleAddToCoCreateSlot}
        onAddLibraryToCoCreateSlot={handleAddLibraryToCoCreateSlot}
        onReplaceLibraryToCoCreateSlot={handleReplaceLibraryInCoCreateSlot}
        onReplaceInCoCreateSlot={handleReplaceInCoCreateSlot}
        onRemoveFromCoCreateSlot={handleRemoveFromCoCreateSlot}
        isCampaignRunning={isCampaignRunning}
        requiredSlotRatios={requiredSlotRatios}
        isGoogleOnly={isGoogleOnly}
        isGoogleSelected={isGoogleSelected}
        bothPlatformsSelected={bothPlatformsSelected}
        googleExactRatios={googleExactRatios}
        onViewGeneratedVariants={() => setGeneratedImagesModalOpen(true)}
        hasGoogleExactRatios={hasGoogleExactRatios}
        showValidationErrors={showValidationErrors}
        missingGoogleExactRatios={missingGoogleExactRatios}
        slotError={slotError}
        setSlotError={setSlotError}
      />


      {media.length > 0 && !readonly && (
        fetchStatusFailed ? (
          <ErrorMessage danger={true} style={{ justifyContent: 'flex-start' }}>
            {t('Recommendations.campaign.setup.coCreatePanel.fetchStatusError')}
          </ErrorMessage>
        ) : (
          <CoCreateImagePanel
            variant={coCreateVariant}
            setVariant={setCoCreateVariant}
            selectedSubGroupId={selectedSubGroupId}
            setSelectedSubGroupId={setSelectedSubGroupId}
            media={media}
            headlines={headlines}
            maxItems={maxItems}
            generatedMediaCount={liveGeneratedMediaCount}
            showGenerationError={generationFailed}
            onGenerate={handleGenerate}
            isCampaignRunning={isCampaignRunning}
            onRequestHeadlines={onRequestHeadlines}
            requiredSlotRatios={requiredSlotRatios}
            onViewVariants={() => setGeneratedImagesModalOpen(true)}
          />
        )
      )}

      <PortalModal
        isOpen={generatedImagesModalOpen}
        onClickOutside={() => !isSubmittingSelections && setGeneratedImagesModalOpen(false)}
        onClose={() => !isSubmittingSelections && setGeneratedImagesModalOpen(false)}
        title={t('Recommendations.campaign.setup.coCreatePanel.generatedImagesModal.title')}
        className="generated-images-portal-modal"
      >
        <GeneratedImagesModal
          variants={generatedVariants}
          closeModal={() => setGeneratedImagesModalOpen(false)}
          onSubmitSelections={async (selectedIds) => {
            setIsSubmittingSelections(true)

            try {
              return await handleSubmitGeneratedSelections(selectedIds)
            } finally {
              setIsSubmittingSelections(false)
            }
          }}
        />
      </PortalModal>

      {!readonly && (
        <UploadButtons
          hasMedia={media.length > 0}
          getInputProps={getInputProps}
          onDeviceUpload={handleDeviceUpload}
          onLibraryUpload={handleLibraryUpload}
          disabled={isCampaignRunning || reachedMaxLimit}
          isCampaignRunning={isCampaignRunning}
        />
      )}

      {showYoutubeUrlInput && !readonly && !isCampaignRunning && !reachedMaxLimit && (
        <div className="youtube-video-input">
          <YouTubeUrlInput video={null} onVideo={addYoutubeVideo} onClear={() => {}} />
          <p className="youtube-video-input__hint">
            {t('Recommendations.campaign.setup.media.youtubeVideo.hint')}
          </p>
        </div>
      )}

      <LibraryPickerModal
        isOpen={isLibraryPickerOpen}
        onClose={() => setIsLibraryPickerOpen(false)}
        onApply={handleLibraryApply}
        selectionMode="multiple"
      />

      {expandedImage && (
        <PortalModal
          isOpen={!!expandedImage}
          className="full-height"
          onClickOutside={() => setExpandedImage(null)}
          title="Media Preview"
        >
          <MediaExpandedModal
            expandedImage={expandedImage}
            setExpandedImage={setExpandedImage}
            onBack={onBack}
            onNext={onNext}
            isFirstItem={isFirstItem}
            isLastItem={isLastItem}
          />
        </PortalModal>
      )}
    </div>
  )
}

MediaUpload.propTypes = {
  initialMedia: PropTypes.arrayOf(object),
  onChange: PropTypes.func.isRequired,
  onClearValidationErrors: PropTypes.func,
  maxItems: PropTypes.number,
  generatedMediaCount: PropTypes.number,
  readonly: PropTypes.bool,
  validationError: PropTypes.bool,
  isCampaignRunning: PropTypes.bool,
  eventId: PropTypes.string,
  tc: PropTypes.string,
  tcRunId: PropTypes.number,
  headlines: PropTypes.arrayOf(PropTypes.string),
  onRequestHeadlines: PropTypes.func,
  showYoutubeUrlInput: PropTypes.bool,
  showValidationErrors: PropTypes.bool,
}

export default MediaUpload
